// Plain substring search/replace over a MARKDOWN string — the coordinate space a part's Y.Text
// is stored in. Used by the wide (artefact/bid) find/replace scope, which has no ProseMirror doc
// to search (a background room is a bare Y.Doc, never mounted into an editor) — see
// lib/wide-replace.ts. The local (this section / this part) scope searches the live editor's
// RENDERED text instead (components/editor/find-in-editor.ts), which is why the two can
// occasionally disagree on content containing literal markdown syntax (`**`, `#`, …): a documented
// simplification, not an oversight — see that module's header.
//
// Deliberately NOT a regex: the query is arbitrary member-typed text that may run across a whole
// bid's artefacts unattended (the wide scope), so it must never be interpreted as a pattern
// (no `.`/`*` meaning something the member did not intend, no catastrophic backtracking).

/** One occurrence of the query, as a half-open window into the searched string. */
export interface FindMatch {
  start: number
  end: number
}

export interface FindReplaceOptions {
  caseSensitive: boolean
}

/**
 * Every non-overlapping occurrence of `query` in `text`, left to right. Case-insensitive matching
 * lower-cases both sides — a simplification that can misplace or miss a match for the rare
 * character whose `toLowerCase()` changes length (e.g. Turkish İ), which JS string search offers
 * no better primitive for without a full Unicode-aware library.
 */
export function findMatches(text: string, query: string, opts: FindReplaceOptions): FindMatch[] {
  if (query === '') return []
  const hay = opts.caseSensitive ? text : text.toLowerCase()
  const needle = opts.caseSensitive ? query : query.toLowerCase()
  const matches: FindMatch[] = []
  let from = 0
  while (from <= hay.length - needle.length) {
    const idx = hay.indexOf(needle, from)
    if (idx === -1) break
    matches.push({ start: idx, end: idx + needle.length })
    from = idx + needle.length
  }
  return matches
}

/**
 * Replace every match with `replacement`. Applied back-to-front so an earlier match's offset is
 * never invalidated by a later one's replacement changing the string's length — the same
 * principle lib/diff.ts#computeChangedRegion's caller (syncYText) relies on for a single window,
 * generalised here to many. `matches` must be sorted ascending and non-overlapping, exactly what
 * `findMatches` returns.
 */
export function applyReplacement(text: string, matches: FindMatch[], replacement: string): string {
  let out = text
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i]!
    out = out.slice(0, m.start) + replacement + out.slice(m.end)
  }
  return out
}

/** Matches wholly inside `[from, to)` — the "this section" sub-scope of a search already run over
 *  the whole text. */
export function matchesWithin(matches: FindMatch[], from: number, to: number): FindMatch[] {
  return matches.filter((m) => m.start >= from && m.end <= to)
}
